import * as React from 'react';
import { ReduxStates } from '../../redux/reduxTypes';
import { useSelector } from 'react-redux';
import { DateFilters, DateRange } from '../../types';
import { returnFormattedOptions } from './helpers';
import { ReactComponent as Calendar } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as DropDown } from '../../assets/icons/graph-table-dropdown-icon.svg';
import moment from 'moment';
import MonthRangePicker from '../MonthRangePicker';
import BaseButton from '../BaseButton';
import { getDates } from '../../helpers/functions';

export interface DropDownFilterProps {
  className?: string;
  label?: string;
  isDateFilter?: boolean;
  overrideStyles?: any;
  onChange: (option: string, customRange?: Array<number | string>) => void;
  options?: Array<any>;
  value: any;
  variant?: string;
  tableGraph?: boolean;
  disabled?: boolean;
  automationID?: string;
  scrollable?: boolean;
  isNotesScreen?: boolean;
}
interface VariantProps {
  [key: string]: {
    [key: string]: string;
  };
}
interface FilterOptionProps {
  disabled?: boolean;
  option: string;
}

const variantStyles: VariantProps = {
  wrapper: {
    containerFullWidth: 'w-full mr-2.5 last:mr-0',
    slicerMenu: 'z-20',
    dataUpload: 'z-20 min-w-[180px]',
    mappingOptions: 'z-auto'
  },
  container: {
    global: 'shadow-[0px 1px 2px rgba(0, 0, 0, 0.1)] px-[13px] min-w-[150px]',
    globalDateFilter: 'shadow-[0px 1px 2px rgba(0, 0, 0, 0.1)] w-[255px] px-[13px]',
    small: 'min-w-[85px] px-[10px]',
    minimal: 'min-w-[65px] px-3',
    default: 'min-w-[50px] px-[13px]'
  },
  filtersWrapper: {
    customRange: 'w-[200%]',
    mappingOptions: 'w-full z-40 max-h-[300px] overflow-y-auto',
    default: 'w-full z-40'
  },
  value: {
    small: 'mr-[5px]',
    default: ''
  },
  option: {
    disabled: 'cursor-default bg-[rgba(20,20,20,0.1)] text-[#898989]',
    default: 'cursor-pointer bg-transparent hover:bg-opaqueNavy focus:bg-opaqueNavy hover:text-navy focus:text-navy'
  }
};

const DropDownFilter: React.FC<DropDownFilterProps> = ({
  className,
  label,
  isDateFilter,
  overrideStyles,
  options,
  onChange,
  value,
  variant,
  tableGraph,
  disabled,
  automationID,
  scrollable,
  isNotesScreen
}) => {
  const [showOptions, setShowOptions] = React.useState<boolean>(false);
  const [showCustomSelection, setShowCustomSelection] = React.useState<boolean>(false);
  const [filteredDates, setFilteredDates] = React.useState<Array<number>>([-1, 0]);

  const data = useSelector((state: ReduxStates) => state.data);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const dates = useSelector((state: ReduxStates) => state.filters.dates);
  const [rangeLimit, setRangeLimit] = React.useState<DateRange[] | null>(data.dateFilterRangeLimit);
  const [heightOffset, setHeightOffset] = React.useState<boolean | null>(false);

  const ref = React.useRef(null);

  const handleClickOutside = (event: Event) => {
    if (ref.current && !(ref.current as any).contains(event.target)) {
      setShowOptions(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  React.useEffect(() => {
    if (
      !isNotesScreen &&
      isDateFilter &&
      data.dateFilterRangeLimit &&
      !dates.custom &&
      dates.customNotes &&
      moment(data.dateFilterRangeLimit[0].startDate).startOf('month').format('YYYY-MM-DD') !==
        moment().clone().startOf('month').format('YYYY-MM-DD')
    ) {
      onChange('Last 3 Months');
    }
  }, [isNotesScreen]);

  React.useEffect(() => {
    if (data.dateFilterRangeLimit) {
      const missingDates = getDates(
        moment(data.dateFilterRangeLimit[0].startDate).startOf('month').format('YYYY-MM-DD'),
        moment().clone().startOf('month').format('YYYY-MM-DD')
      );
      missingDates.shift();
      const dateRanges = missingDates.reverse().map((date) => {
        return {
          startDate: moment(date).startOf('month').format('YYYY-MM-DD'),
          endDate: moment(date).endOf('month').format('YYYY-MM-DD')
        };
      });
      setRangeLimit([...dateRanges, ...data.dateFilterRangeLimit]);
    }
  }, [data.dateFilterRangeLimit]);

  const handleRangeOptionRendering = () => {
    const formattedRange: Array<DateFilters> | null = returnFormattedOptions(
      data.dateFilterRangeLimit,
      copyData,
      isNotesScreen
    );

    if (formattedRange) {
      return (
        <div className={`w-full p-0.5 rounded-[0px 0px 1px 1px] border-r border-[#e1e1e1]`}>
          {formattedRange?.map((filter, index: number) => {
            return <FilterOption key={index} disabled={filter.disabled} option={filter.option} />;
          })}
        </div>
      );
    }
  };

  const handleOptionSelection = (option: string) => {
    if (option === 'Custom Range' && isDateFilter) {
      setShowCustomSelection(true);
      return true;
    }

    setShowOptions(false);
    setShowCustomSelection(false);

    return onChange(option);
  };

  const dropdownClickHandler = (event: any) => {
    const bodyRect = document.body.getBoundingClientRect(),
      elemRect = event.target.getBoundingClientRect(),
      offset = elemRect.top - bodyRect.top,
      docHeight = document.body.scrollHeight;
    if (offset + 50 > docHeight) {
      setHeightOffset(true);
    } else {
      heightOffset && setHeightOffset(false);
    }
  };

  const containerClasses = `relative cursor-pointer flex items-center justify-between bg-white box-border rounded-[4px] ${
    tableGraph ? 'h-[30px]' : automationID === 'records_per_page_picker' ? 'h-[25px]' : 'h-[40px]'
  }`;
  const OptionClasses = `m-0 font-nunitoRegular py-[13px] px-[10px] text-[14px] w-full border-none text-left hover:rounded-[2px] focus:rounded-[2px] last:border-0`;

  const FilterOption: React.FC<FilterOptionProps> = ({ disabled, option }) => (
    <div
      className={`${OptionClasses} ${disabled ? variantStyles.option.disabled : variantStyles.option.default}`}
      onClick={() => {
        if (!disabled) handleOptionSelection(option);
      }}
    >
      {option}
    </div>
  );

  return (
    <>
      {data.dateFilterRangeLimit && isDateFilter && (
        <span
          id="global_reporting-data-through_text"
          className="text-[#5b5b5b] text-xs text-right font-nunitoMedium mr-3"
        >
          {copyData?.reporting_data_label} <br />
          {!isNotesScreen
            ? moment(data.dateFilterRangeLimit[0].endDate).format('MMM DD, YYYY')
            : moment().clone().endOf('month').format('MMM DD, YYYY')}
        </span>
      )}
      <div
        ref={ref}
        id={automationID}
        className={`${className || ''} relative z-30 ${(variant && variantStyles.wrapper[variant]) || ''}`}
      >
        {label && (
          <div className={`block text-xs text-[#1b1b1b] opacity-70 mb-[5px] font-nunitoBold leading-[18px]`}>
            {label}
          </div>
        )}
        <div
          className={`
            ${containerClasses} ${(variant && variantStyles.container[variant]) || variantStyles.container.default}
            ${disabled ? 'opacity-50' : ''}
          `}
          style={{
            border: '1px solid rgba(0, 59, 88, 0.21)',
            ...overrideStyles
          }}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
            } else {
              !showOptions && dropdownClickHandler(e);
              setShowOptions(!showOptions);
            }
          }}
        >
          <div className={`flex items-center ${(variant && variantStyles.value[variant]) || ''}`}>
            {isDateFilter && <Calendar className="mr-[9.5px]" />}
            <span className="text-[14px] font-nunitoLight">{value}</span>
          </div>
          <DropDown />
        </div>
        {showOptions && (
          <div
            className={`absolute right-0 filter drop-shadow-dropdownFilter w-full
            ${heightOffset ? 'bottom-[30px]' : variant && variant === `minimal` ? `top-0` : `top-auto`}
              ${variant && variantStyles.filtersWrapper[showCustomSelection ? 'customRange' : variant || 'default']}`}
          >
            <div className={`flex w-full border border-[#e5e5ea] bg-white rounded-[2px]`}>
              {isDateFilter && handleRangeOptionRendering()}
              {!isDateFilter && (
                <div
                  className={`rounded-b-[1px] w-full p-[3px] 
                  ${scrollable ? 'overflow-y-scroll max-h-[380px] custom-scrollbar' : ''}`}
                >
                  {options?.map((option: string, index: number) => (
                    <FilterOption key={index} option={option} />
                  ))}
                </div>
              )}
              {showCustomSelection && data.dateFilterRangeLimit && (
                <div className="w-[1200px] border-l-[1px solid rgba(27, 27, 27, 0.1)] flex flex-col justify-center items-center">
                  <MonthRangePicker
                    rangeLimit={isNotesScreen && rangeLimit ? rangeLimit : data.dateFilterRangeLimit}
                    value={filteredDates}
                    onChange={(values) => setFilteredDates(values)}
                  />
                </div>
              )}
            </div>
            {showCustomSelection && (
              <div className="flex justify-end bg-white px-[22px] py-[17px] border border-[#e5e5ea]">
                <BaseButton
                  onClick={() => {
                    setFilteredDates([-1, 0]);
                    setShowCustomSelection(false);
                  }}
                  variant="secondary"
                >
                  {copyData?.clear_button}
                </BaseButton>
                <BaseButton
                  onClick={() => {
                    if (data.dateFilterRangeLimit) {
                      const endDate = moment(
                        isNotesScreen && rangeLimit
                          ? rangeLimit[Math.abs(filteredDates[0])].startDate
                          : data.dateFilterRangeLimit[Math.abs(filteredDates[0])].startDate
                      ).format('MMM YYYY');
                      const startDate = moment(
                        isNotesScreen && rangeLimit
                          ? rangeLimit[Math.abs(filteredDates[1])].startDate
                          : data.dateFilterRangeLimit[Math.abs(filteredDates[1])].startDate
                      ).format('MMM YYYY');

                      setShowCustomSelection(false);
                      setShowOptions(false);

                      onChange('Custom Range', [endDate, startDate]);
                    }
                  }}
                >
                  {copyData?.apply_button}
                </BaseButton>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DropDownFilter;
